/**
 * Sysadmin Settings → Email (SMTP) — F-W1a-03.1 / F-W1a-06.2.
 *
 * Surfaces the platform-default outgoing email transport.  When the
 * sysadmin saves a host + credentials + flips Enabled, the invite /
 * password-reset flows (PR #1139 contract) start actually sending mail
 * instead of returning `emailDelivery: 'disabled'`.
 *
 * Surface:
 *   - Form: host / port / username / password / secure-mode /
 *     from address / from name / reply-to / enabled toggle /
 *     delivery mode (required | best_effort | disabled).
 *   - Save button — PUT /admin/platform/settings/email.
 *   - "Send test email" panel — POST /test with a sysadmin-supplied
 *     recipient.  Shows delivery / transport / errorCode inline.
 *
 * Password input is write-only: shows "•••••• (configured)" if a
 * password is already set, with a "Replace" button that clears the
 * field for entry.  Submitting an empty replace-field clears the
 * stored password.  Submitting a non-empty value encrypts it
 * server-side.
 */

import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Mail,
  Save,
  Send,
  Lock,
  CheckCircle2,
  AlertCircle,
  Power,
  Loader2,
  KeyRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SectionCard } from '@/components/ui/section-card';
import { SettingField } from '@/components/ui/setting-field';
import { PageError } from '@/components/ui/page-error';
import {
  useSysadminPlatformEmail,
  useUpdatePlatformEmail,
  useSendPlatformEmailTest,
  type PlatformEmailConfig,
  type PlatformEmailTestResult,
  type EmailDeliveryMode,
  type EmailSecure,
} from '@/lib/use-sysadmin-settings';

export const Route = createFileRoute('/sysadmin/settings/email')({
  component: EmailSettingsPage,
});

interface FormState {
  enabled: boolean;
  deliveryMode: EmailDeliveryMode;
  host: string;
  port: number;
  username: string;
  /** undefined = preserve existing, '' = clear, other = set new. */
  password: string | undefined;
  passwordTouched: boolean;
  secure: EmailSecure;
  fromAddress: string;
  fromName: string;
  replyTo: string;
}

function configToForm(c: PlatformEmailConfig): FormState {
  return {
    enabled: c.enabled,
    deliveryMode: c.deliveryMode,
    host: c.host ?? '',
    port: c.port,
    username: c.username ?? '',
    password: undefined,
    passwordTouched: false,
    secure: c.secure,
    fromAddress: c.fromAddress ?? '',
    fromName: c.fromName ?? '',
    replyTo: c.replyTo ?? '',
  };
}

function EmailSettingsPage() {
  const { data, error, isLoading, refetch } = useSysadminPlatformEmail();
  const updateMutation = useUpdatePlatformEmail();
  const testMutation = useSendPlatformEmailTest();

  const [form, setForm] = useState<FormState | null>(null);
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<PlatformEmailTestResult | null>(
    null,
  );
  const [saveError, setSaveError] = useState<string | null>(null);

  // Hydrate form on first successful load.
  useEffect(() => {
    if (data && !form) {
      setForm(configToForm(data));
    }
  }, [data, form]);

  const passwordConfigured = data?.passwordConfigured ?? false;

  const canSave = useMemo(() => {
    if (!form) return false;
    if (form.enabled) {
      // To enable, host + from must be set.
      if (!form.host.trim()) return false;
      if (!form.fromAddress.trim()) return false;
    }
    return true;
  }, [form]);

  async function handleSave() {
    if (!form) return;
    setSaveError(null);
    try {
      const payload: Record<string, unknown> = {
        enabled: form.enabled,
        deliveryMode: form.deliveryMode,
        host: form.host.trim() === '' ? null : form.host.trim(),
        port: form.port,
        username: form.username.trim() === '' ? null : form.username.trim(),
        secure: form.secure,
        fromAddress:
          form.fromAddress.trim() === '' ? null : form.fromAddress.trim(),
        fromName: form.fromName.trim() === '' ? null : form.fromName.trim(),
        replyTo: form.replyTo.trim() === '' ? null : form.replyTo.trim(),
      };
      // Only include password when the user explicitly touched it.
      if (form.passwordTouched) {
        payload.password = form.password ?? '';
      }
      const next = await updateMutation.mutateAsync(payload as never);
      setForm(configToForm(next));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  async function handleSendTest() {
    if (!testTo.trim()) return;
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync({ to: testTo.trim() });
      setTestResult(result);
    } catch (err) {
      setTestResult({
        delivery: 'failed',
        transport: 'none',
        error: err instanceof Error ? err.message : 'Test send failed',
      });
    }
  }

  if (error) {
    return (
      <PageError
        title="Failed to load email settings"
        message={error.message}
        onRetry={refetch}
      />
    );
  }

  if (isLoading || !form || !data) {
    return (
      <div className="flex items-center justify-center p-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading email settings…
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Mail className="h-6 w-6" />
          Outgoing Email (SMTP)
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Platform-default SMTP transport. Used by invitation, password-reset,
          and notification emails for tenants that do not have their own SMTP
          integration configured.
        </p>
      </div>

      {/* Status banner */}
      <div
        className={`rounded-lg border p-4 flex items-start gap-3 ${
          data.enabled
            ? 'border-green-200 bg-green-50 dark:border-green-900/40 dark:bg-green-950/30'
            : 'border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30'
        }`}
      >
        {data.enabled ? (
          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
        ) : (
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
        )}
        <div className="flex-1 text-sm">
          {data.enabled ? (
            <>
              <strong>Outgoing email is enabled.</strong> Mode:{' '}
              <code className="text-xs">{data.deliveryMode}</code>. Configured
              host: <code className="text-xs">{data.host ?? '—'}</code>.
            </>
          ) : (
            <>
              <strong>Outgoing email is disabled.</strong> Invitations and
              password resets currently return{' '}
              <code className="text-xs">emailDelivery: 'disabled'</code>;
              admins share invitation links manually. Configure SMTP below and
              flip the Enabled switch.
            </>
          )}
        </div>
      </div>

      {/* Save error banner */}
      {saveError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          {saveError}
        </div>
      ) : null}

      {/* SMTP server */}
      <SectionCard
        title="SMTP server"
        description="Where ArgusAI submits outgoing email."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SettingField label="Host" className="md:col-span-2">
            <Input
              type="text"
              placeholder="smtp.sendgrid.net"
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              autoComplete="off"
            />
          </SettingField>
          <SettingField label="Port">
            <Input
              type="number"
              min={1}
              max={65535}
              value={form.port}
              onChange={(e) =>
                setForm({
                  ...form,
                  port: Number.parseInt(e.target.value, 10) || 587,
                })
              }
            />
          </SettingField>
          <SettingField
            label="Secure mode"
            description="STARTTLS = port 587 (default).  TLS = port 465 implicit.  None = dev only."
          >
            <Select
              value={form.secure}
              onValueChange={(v) => setForm({ ...form, secure: v as EmailSecure })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="starttls">STARTTLS</SelectItem>
                <SelectItem value="tls">TLS (implicit)</SelectItem>
                <SelectItem value="none">None (insecure)</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>
        </div>
      </SectionCard>

      {/* Authentication */}
      <SectionCard
        title="Authentication"
        description="Leave both blank for unauthenticated relays."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SettingField label="Username">
            <Input
              type="text"
              placeholder="apikey"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              autoComplete="off"
            />
          </SettingField>
          <SettingField label="Password">
            {passwordConfigured && !form.passwordTouched ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 rounded-md border bg-muted text-muted-foreground text-sm flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5" />
                  •••••• (configured)
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setForm({ ...form, passwordTouched: true, password: '' })
                  }
                >
                  <KeyRound className="h-4 w-4 mr-1" />
                  Replace
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="password"
                  placeholder={
                    passwordConfigured
                      ? 'New password (blank to clear)'
                      : 'Password'
                  }
                  value={form.password ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      passwordTouched: true,
                      password: e.target.value,
                    })
                  }
                  autoComplete="new-password"
                />
                {passwordConfigured && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setForm({
                        ...form,
                        passwordTouched: false,
                        password: undefined,
                      })
                    }
                  >
                    Cancel
                  </Button>
                )}
              </div>
            )}
          </SettingField>
        </div>
      </SectionCard>

      {/* From identity */}
      <SectionCard
        title="From identity"
        description="The mailbox recipients see in their inbox."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SettingField label="From address">
            <Input
              type="email"
              placeholder="noreply@argusai.viaanix.com"
              value={form.fromAddress}
              onChange={(e) =>
                setForm({ ...form, fromAddress: e.target.value })
              }
              autoComplete="off"
            />
          </SettingField>
          <SettingField label="From name">
            <Input
              type="text"
              placeholder="ArgusAI Platform"
              value={form.fromName}
              onChange={(e) => setForm({ ...form, fromName: e.target.value })}
              autoComplete="off"
            />
          </SettingField>
          <SettingField
            label="Reply-To (optional)"
            className="md:col-span-2"
            description="If set, replies route to this address instead of the From mailbox."
          >
            <Input
              type="email"
              placeholder="support@argusai.viaanix.com"
              value={form.replyTo}
              onChange={(e) => setForm({ ...form, replyTo: e.target.value })}
              autoComplete="off"
            />
          </SettingField>
        </div>
      </SectionCard>

      {/* Delivery semantics */}
      <SectionCard
        title="Delivery"
        description="Master switch + how SMTP failures propagate to callers."
      >
        <div className="space-y-4">
          <SettingField
            label="Enabled"
            description="When OFF, all invite/reset/notification emails skip SMTP and admins share links manually."
          >
            <div className="flex items-center gap-3">
              <Switch
                checked={form.enabled}
                onCheckedChange={(v) => setForm({ ...form, enabled: v })}
              />
              <Badge variant={form.enabled ? 'default' : 'secondary'}>
                <Power className="h-3 w-3 mr-1" />
                {form.enabled ? 'On' : 'Off'}
              </Badge>
            </div>
          </SettingField>
          <SettingField
            label="Delivery mode"
            description="best_effort: SMTP failures do not block invite/reset.  required: rare — fails the caller if SMTP errors.  disabled: same as switching off."
          >
            <Select
              value={form.deliveryMode}
              onValueChange={(v) =>
                setForm({ ...form, deliveryMode: v as EmailDeliveryMode })
              }
            >
              <SelectTrigger className="max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="best_effort">
                  Best-effort (recommended)
                </SelectItem>
                <SelectItem value="required">Required (strict)</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </SettingField>
        </div>
      </SectionCard>

      {/* Save bar */}
      <div className="flex items-center justify-between border-t pt-4">
        <div className="text-sm text-muted-foreground">
          {updateMutation.isPending ? 'Saving…' : 'Changes are saved when you click Save.'}
        </div>
        <Button onClick={handleSave} disabled={!canSave || updateMutation.isPending}>
          {updateMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save
        </Button>
      </div>

      {/* Test send */}
      <SectionCard
        title="Send test email"
        description="Verify the configured transport.  Uses the LIVE saved config — Save first if you just changed anything."
      >
        <div className="flex items-end gap-3">
          <SettingField label="To" className="flex-1">
            <Input
              type="email"
              placeholder="you@example.com"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              autoComplete="off"
            />
          </SettingField>
          <Button
            onClick={handleSendTest}
            disabled={!testTo.trim() || testMutation.isPending}
          >
            {testMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send test
          </Button>
        </div>

        {testResult && (
          <div
            className={`mt-4 rounded-md border p-3 text-sm flex items-start gap-2 ${
              testResult.delivery === 'sent'
                ? 'border-green-200 bg-green-50 dark:border-green-900/40 dark:bg-green-950/30'
                : testResult.delivery === 'disabled'
                  ? 'border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30'
                  : 'border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30'
            }`}
          >
            {testResult.delivery === 'sent' ? (
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5" />
            ) : (
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
            )}
            <div className="flex-1">
              <div>
                Result: <strong>{testResult.delivery}</strong> via{' '}
                <code className="text-xs">{testResult.transport}</code>
                {testResult.messageId ? (
                  <>
                    {' '}
                    (id <code className="text-xs">{testResult.messageId}</code>)
                  </>
                ) : null}
              </div>
              {testResult.errorCode ? (
                <div className="text-xs mt-1">
                  Code: <code>{testResult.errorCode}</code>
                </div>
              ) : null}
              {testResult.error ? (
                <div className="text-xs mt-1 text-red-700 dark:text-red-300">
                  {testResult.error}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
